import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';

interface TooltipProps {
  /** 툴팁 내용 */
  content: React.ReactNode;
  /** 트리거 요소 */
  children: React.ReactNode;
  /** 툴팁 위치 (기본: bottom) */
  placement?: 'top' | 'bottom';
  /** 수평 정렬 (기본: left) */
  align?: 'left' | 'center' | 'right';
}

export function Tooltip({
  content,
  children,
  placement = 'bottom',
  align = 'left',
}: TooltipProps) {
  const [isVisible, setIsVisible] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const triggerRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const touchTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const isTouchRef = useRef(false);

  const handleTouchStart = useCallback(() => {
    isTouchRef.current = true;
    clearTimeout(touchTimerRef.current);
    setIsVisible(true);
    touchTimerRef.current = setTimeout(() => {
      setIsVisible(false);
      isTouchRef.current = false;
    }, 2000);
  }, []);

  useEffect(() => {
    return () => clearTimeout(touchTimerRef.current);
  }, []);

  useEffect(() => {
    if (isVisible && triggerRef.current && tooltipRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      const tooltipWidth = tooltipRef.current.offsetWidth;
      const tooltipHeight = tooltipRef.current.offsetHeight;
      const viewportWidth = window.innerWidth;
      const MARGIN = 8; // 화면 가장자리 여백

      // 세로 위치 계산
      const top = placement === 'bottom'
        ? rect.bottom + 4
        : rect.top - tooltipHeight - 4;

      // 가로 위치 계산 (정렬 방식에 따라)
      let left: number;
      switch (align) {
        case 'right':
          // 트리거 오른쪽 끝에 툴팁 오른쪽 정렬
          left = rect.right - tooltipWidth;
          break;
        case 'center':
          // 트리거 중앙에 툴팁 중앙 정렬
          left = rect.left + (rect.width / 2) - (tooltipWidth / 2);
          break;
        case 'left':
        default:
          // 트리거 왼쪽 끝에 툴팁 왼쪽 정렬
          left = rect.left;
          break;
      }

      // 화면 경계 체크 및 조정
      // 오른쪽 경계 초과 시
      if (left + tooltipWidth > viewportWidth - MARGIN) {
        left = viewportWidth - tooltipWidth - MARGIN;
      }
      // 왼쪽 경계 초과 시
      if (left < MARGIN) {
        left = MARGIN;
      }

      setPosition({ top, left });
    }
  }, [isVisible, placement, align]);

  return (
    <>
      <div
        ref={triggerRef}
        onMouseEnter={() => setIsVisible(true)}
        onMouseLeave={() => { if (!isTouchRef.current) setIsVisible(false); }}
        onTouchStart={handleTouchStart}
      >
        {children}
      </div>

      {isVisible && createPortal(
        <div
          ref={tooltipRef}
          style={{
            position: 'fixed',
            top: position.top,
            left: position.left,
          }}
          className="z-[9999] bg-gray-800 text-white text-xs rounded-card-sm py-2 px-3 max-w-xs shadow-lg animate-fade-in"
          onMouseEnter={() => setIsVisible(true)}
          onMouseLeave={() => setIsVisible(false)}
        >
          {content}
        </div>,
        document.body
      )}
    </>
  );
}

/** @deprecated PortalTooltip → Tooltip로 이름 변경됨. 하위 호환용 re-export */
export const PortalTooltip = Tooltip;
export default Tooltip;
